import { localizedStrings } from '@core/strings'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useModal } from '@common/GlobalModal'
import { DialogContent, Button, Card } from '@common/material'
import { Box, Flex, Text } from '@fundamentals'
import { FormControlLabel, Switch } from '@mui/material'
import { useGetProjectPlan } from '@core/react-query/features/projects/plans'
import { useNavigationContext } from '@common/hooks'
import { useGetPlanPermits } from '@core/react-query/features/permits/plans'
import { usePermitTemplatesFilter } from '@core/providers/filters/usePermitTemplatesFilter'
import { useTextInputFilter } from '@core/providers/filters'
import { Filters } from 'webapp/components/elements/Filters/Filters'
import { useDateRangeFilter } from '@core/providers/filters'
import { ProjectPlanPermitsSidebar } from '@modules/projectPlan/components/ProjectPlanPermitsSidebar'
import { usePermitStatusFilter } from '@core/providers/filters/usePermitStatusFilter'
import { ModalDialogContainer } from '@common/modal'
import { CanvasState } from '@core/entities/skia/CanvasState'
import dynamic from 'next/dynamic'
import { ProjectPlan } from '@core/entities/project/ProjectPlan'
import { PROJECT_PLAN_AREA_COLORS } from '@core/entities/project'
import { ProjectPlanPermits } from '@core/entities/project/ProjectPlanPermits'
import { Pin } from '@core/entities/skia/Pin'
import { ProjectPlanZonesSidebarWithPins } from '../components/ProjectPlanZonesSidebarWithPins'
import { Permit } from '@core/entities/permit/Permit'
import { UnsavedChangesProjectPlanZonesModal } from './UnsavedChangesProjectPlanZonesModal'
import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import { useQueryParamFilterStore } from '@elements/Filters/store'
import { PermitStatus } from '@core/entities/permit/PermitBase'

const SkiaPlanView = dynamic(() =>
  import('@modules/skia/SkiaPlanView').then((mod) => mod.SkiaPlanView),
)

interface ComponentType {
  nodeId: string
  projectId: number
  planId: number
  nodePath: string
  initialSelectedAreas?: string[]
  initialSelectedPins?: Pin[]
  permit?: Permit
  readOnly?: boolean
  onSuccess?: (selectedAreaIds: string[], createdPins: Pin[]) => void
  plan?: ProjectPlan
  enablePinCreation?: boolean
}

const initialCanvasState = CanvasState.new({
  mode: 'VIEW',
  shapes: [],
  multipleSelection: true,
})

export const INITIAL_STATUSES: PermitStatus[] = [
  'OPEN',
  'REQUESTED',
  'ISSUED',
  'EXTENSION_REQUESTED',
  'SENT_TO_PERMIT_HOLDER',
]

export const ProjectPlanPermitsModal: React.FC<ComponentType> = ({
  projectId,
  nodeId,
  planId,
  nodePath,
  initialSelectedAreas = [],
  initialSelectedPins = [],
  permit,
  readOnly = false,
  onSuccess,
  plan,
  enablePinCreation = false,
}) => {
  const { organisationId } = useNavigationContext()
  const router = useRouter()
  const [canvasState, setCanvasState] =
    useState<CanvasState>(initialCanvasState)
  const [showPins, setShowPins] = useState(true)
  const { hideModal, showModal } = useModal()

  // Sync URL query params with canvas state for analytics tracking
  const syncUrlParams = useCallback(
    (state: CanvasState) => {
      const urlParams = state.getUrlParams()
      const query = { ...router.query }

      if (urlParams.selectedZones) {
        query.selectedZones = urlParams.selectedZones
      } else {
        delete query.selectedZones
      }

      if (urlParams.selectedPermit) {
        query.selectedPermit = urlParams.selectedPermit
      } else {
        delete query.selectedPermit
      }

      router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
      })
    },
    [router],
  )

  // Wrapper to sync canvas state changes to URL
  const handleCanvasStateChange = useCallback(
    (newState: CanvasState) => {
      setCanvasState(newState)
      syncUrlParams(newState)
    },
    [syncUrlParams],
  )

  // Clear URL params when modal closes
  const clearUrlParams = useCallback(() => {
    const query = { ...router.query }
    delete query.planId
    delete query.selectedZones
    delete query.selectedPermit
    delete query.statuses
    delete query.dateRange
    delete query.permitUUID
    delete query.templateIds

    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    })
  }, [router])

  const { data: projectPlan } = useGetProjectPlan(
    {
      projectId,
      nodeId,
      planId,
    },
    {
      refetchOnMount: !plan,
    },
  )

  const createdPins = canvasState.getNewPins()

  const selectedShapesWithPins = canvasState
    .getShapesWithNewPins()
    .map((shape) => shape.groupId)

  const selectedShapes = selectedShapesWithPins

  const filters = {
    statuses: usePermitStatusFilter({
      multiple: true,
    }),
    dateRange: useDateRangeFilter({
      label: localizedStrings.dateRange,
      dataTest: 'date-range-filter',
    }),
    templateIds: usePermitTemplatesFilter({
      label: localizedStrings.permitTemplate,
      scope: {
        type: 'PROJECT',
        projectId,
        organisationId,
      },
      multiple: true,
    }),
    permitUUID: useTextInputFilter({
      label: localizedStrings.uniqueCode,
    }),
  }

  const queryParamsStore = useQueryParamFilterStore({
    filters,
    queryParams: {
      statuses: 'statuses',
      dateRange: 'dateRange',
      permitUUID: 'permitUUID',
      templateIds: 'templateIds',
    },
  })

  const { dateRange, statuses, ...restFilters } = queryParamsStore.values

  // Need to set default filter values like this due to Modal component not being able to handle the initial values
  useEffect(() => {
    queryParamsStore.setFilterValues({
      statuses: INITIAL_STATUSES,
    })
  }, [])

  const filterQueries = {
    ...(statuses?.length && { statuses }),
    ...(dateRange?.startTime && { startTime: dateRange?.startTime }),
    ...(dateRange?.endTime && { endTime: dateRange?.endTime }),
    ...(permit?.id && { permitIds: [permit?.id] }),
    ...restFilters,
  }

  const { data: planPermits } = useGetPlanPermits(
    {
      planId,
      projectId,
      ...filterQueries,
    },
    {
      enabled: !!projectPlan || !!plan,
      onSuccess: (planPermitsResponse) => {
        const planPermitsEntity = ProjectPlanPermits.new(planPermitsResponse)
        if (!projectPlan) return

        const shapes = projectPlan.areas.map((area) => {
          const shape = area.getPlanAreaShape(
            projectPlan.getDocumentImageTransformData('SCALE_16K'),
          )

          // Set shape properties
          shape.color =
            PROJECT_PLAN_AREA_COLORS.find(
              (areaColor) => areaColor.enum === area?.color,
            )?.hex || area?.color
          shape.groupId = area.uuid
          shape.name = area.name
          shape.description = `${planPermitsEntity?.permitStats?.getAreaTotalElements(
            area.uuid,
          )} ${localizedStrings.permits}`

          // Set initial selection state
          if (initialSelectedAreas.includes(area.uuid)) {
            shape.isActive = true
            shape.isInitial = true
          }

          if (planPermitsEntity?.isShapeSelected(area.uuid)) {
            shape.isActive = true
          }

          return shape
        })

        const currentPermit = planPermitsEntity.getPermit(permit?.id)

        const entityPins = initialSelectedPins.length
          ? initialSelectedPins
          : currentPermit?.projectLocations?.pins?.[0]?.droppedPins?.map(
              (pin) => Pin.new({ ...pin, isInitial: true }),
            ) ?? []

        const pins = planPermitsEntity.getPins(entityPins, permit?.id)

        const canvasBounds =
          projectPlan?.getDocumentImageDimensions('SCALE_16K')

        setCanvasState(
          CanvasState.new({
            mode: 'VIEW',
            shapes,
            multipleSelection: true,
            pins,
            canvasBounds,
          }),
        )
      },
    },
  )

  const currentPermit = planPermits?.getPermit(permit?.id)

  const hasUnsavedChanges = useMemo(() => {
    const initialPinsUuids = initialSelectedPins.map((pin) => pin.uuid)
    const createdPinsUuids = createdPins.map((pin) => pin.uuid)

    return (
      !isEqual(initialSelectedAreas, selectedShapes) ||
      !isEqual(initialPinsUuids, createdPinsUuids)
    )
  }, [initialSelectedAreas, selectedShapes, createdPins])

  const isReadOnly = !plan || readOnly

  return (
    <ModalDialogContainer fullScreen closeOnBack>
      <Card sx={{ p: 2, borderRadius: 0, width: '100%', minHeight: '4.4rem' }}>
        <Flex alignItems='center' justifyContent='space-between'>
          <Text variant='h5'>{nodePath}</Text>

          <Box>
            <Button
              variant='outlined'
              onClick={() => {
                if (hasUnsavedChanges && !isReadOnly) {
                  showModal(
                    <UnsavedChangesProjectPlanZonesModal
                      onDiscard={() => {
                        clearUrlParams()
                        hideModal()
                        onSuccess?.(initialSelectedAreas, initialSelectedPins)
                      }}
                    />,
                  )
                } else {
                  clearUrlParams()
                  hideModal()
                }
              }}
            >
              {isReadOnly ? localizedStrings.close : localizedStrings.cancel}
            </Button>
            {!isReadOnly && (
              <Button
                variant='contained'
                sx={{ ml: 1 }}
                disabled={!hasUnsavedChanges}
                onClick={() => {
                  clearUrlParams()
                  onSuccess?.(selectedShapes, createdPins)
                  hideModal()
                }}
                data-test='close-permit-submit'
              >
                {localizedStrings.save}
              </Button>
            )}
          </Box>
        </Flex>
      </Card>
      <DialogContent sx={{ p: 0 }}>
        <Flex
          mt={1}
          mx={1}
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Filters filters={filters} store={queryParamsStore} />
          <FormControlLabel
            control={
              <Switch
                checked={showPins}
                onChange={(_, checked) => setShowPins(checked)}
              />
            }
            label={localizedStrings.showPins}
          />
        </Flex>

        <Flex flexDirection='column' height='calc(100vh - 125px)'>
          <Flex
            flex={1}
            height='100%'
            width='100%'
            justifyContent='space-between'
          >
            <ProjectPlanZonesSidebarWithPins
              planPermits={planPermits}
              canvasState={canvasState}
              onStateChange={handleCanvasStateChange}
              multipleSelection
            />

            <SkiaPlanView
              imageUrl={projectPlan?.getDocumentImageUrl('SCALE_8K')}
              images={projectPlan?.siteBoxPlanDocumentVersion?.imagesData}
              coordinateSystemSize={projectPlan?.getDocumentImageDimensions(
                'SCALE_16K',
              )}
              state={canvasState}
              setState={handleCanvasStateChange}
              readOnly={readOnly}
              enablePinCreation={enablePinCreation}
              showPins={showPins}
            />

            <ProjectPlanPermitsSidebar
              organisationId={organisationId}
              canvasState={canvasState}
              permitId={permit?.id}
              currentPermit={currentPermit}
              selectedByPinPermit={planPermits?.getPermitByPin(
                canvasState.getSelectedPin(),
              )}
              planPermits={planPermits}
              setCanvasState={handleCanvasStateChange}
            />
          </Flex>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
